import type { PrecioTipo } from '../shared/types/autorizacion';
import type { GrainGroup } from '../shared/types/grain';
import type { EmissionScheduleClient, VentaAutorizacionPayload } from '../shared/types/venta-form';
import { TERMINOS_VERSION } from '../shared/constants/firma-legal';
import { computeFechaVencimiento } from '../shared/utils/emision-schedule';
import { fmtKg } from '../shared/utils/format';
import { mergeObservacionParts } from '../shared/utils/observacion';
import { allocateKilosAcrossCosechas } from './grain-groups';

export interface BuildPayloadContext {
  accountId: string;
  accountLabel: string;
  apiBase: string;
  precioTipo: PrecioTipo;
  selectedGrain: GrainGroup;
  emissionSchedule: EmissionScheduleClient | null;
  metaExtras?: Pick<VentaAutorizacionPayload['meta'], 'creadoPor' | 'adminUser'>;
  /** Solo alta admin web: se concatena a la observación automática. */
  observacionManual?: string | null;
  /** Panel admin: permite kilos por encima de la disponibilidad (app socio sigue bloqueada). */
  allowExceedAvailability?: boolean;
}

export function buildAutorizacionPayload(
  ctx: BuildPayloadContext,
  kilos: number,
  precio: number | null,
  diasVencimiento: number | null,
  firmaDataUrl: string | null,
  aceptoTerminos = false
): VentaAutorizacionPayload {
  const { selectedGrain, precioTipo, emissionSchedule } = ctx;
  const saldoNegativo = selectedGrain.balanceTotal - selectedGrain.availableKg;
  const observacionAuto =
    saldoNegativo < 0 ? 'Saldo negativo: ' + fmtKg(saldoNegativo) + ' kg' : null;
  const observacionManual = ctx.observacionManual?.trim() || null;
  const observacion = mergeObservacionParts(observacionAuto, observacionManual);
  const allocation = allocateKilosAcrossCosechas(selectedGrain.cosechas, kilos, {
    allowOverflow: ctx.allowExceedAvailability === true,
  });

  if (allocation.kilosSinAsignar > 0) {
    throw new Error(
      'No alcanza la disponibilidad por cosecha. Faltan ' +
        fmtKg(allocation.kilosSinAsignar) +
        ' kg por asignar.'
    );
  }

  const cosechasADescontar = allocation.allocations.map((a) => ({
    idCosecha: a.idCosecha,
    nombre: a.cosecha,
    kilos: a.kilos,
    disponibleKg: a.disponibleKg,
    balance: a.balance,
  }));

  const fechaEmision = emissionSchedule?.fechaEmision ?? null;
  const fechaVencimiento = computeFechaVencimiento(
    fechaEmision,
    precioTipo,
    precioTipo === 'base' ? diasVencimiento : null
  );

  return {
    type: 'autorizacion_venta',
    fechaEmision,
    origen: {
      accountId: ctx.accountId,
      accountLabel: ctx.accountLabel || null,
    },
    grano: selectedGrain.grain,
    kilos,
    precioTipo,
    precio: precioTipo === 'base' ? precio : null,
    importeEstimado: precioTipo === 'base' && precio ? precio * kilos : null,
    vencimientoDias: precioTipo === 'base' ? diasVencimiento : null,
    fechaVencimiento,
    observacion,
    disponibilidad: {
      grano: selectedGrain.grain,
      availableKg: selectedGrain.availableKg,
      balanceTotalKg: selectedGrain.balanceTotal,
    },
    cosechas: cosechasADescontar,
    firma: {
      dataUrl: firmaDataUrl,
      firmadoAt: new Date().toISOString(),
      aceptoTerminos,
      terminosVersion: TERMINOS_VERSION,
    },
    meta: {
      apiBase: ctx.apiBase,
      enviadoAt: new Date().toISOString(),
      ...ctx.metaExtras,
      observacionAuto,
      observacionManual,
    },
  };
}
